import {debounce} from '@shared/helpers/function'

import {EntryType, LogLevelType, PageSetType} from '@web/core/constants'

import {postLog} from './api'

type SendToNeloType<T extends object> = {pageSet: Maybe<PageSetType>; entry: Maybe<EntryType>} & T

/**
 * makeSendToNelo 대체를 위한 함수
 **/
const sendToNelo = <
    T extends SendToNeloType<{
        message?: string
        code?: string
        state?: string
        level?: LogLevelType
    }>,
>({
    level,
    type,
}: {
    level?: LogLevelType
    type?: string
}) => {
    return ({pageSet, entry, code, message, state, ...props}: T) => {
        postLog({
            entry,
            code,
            info: props,
            level: props?.level ?? level,
            message,
            state,
            type,
            pageSet,
        })
    }
}

/** 로그 추적을 쉽게 하기 위해 stack 으로 발생한 파일위치를 전달합니다. */
function getErrorTrace(error: Error) {
    try {
        if (error instanceof Error) {
            return {
                message: error.message,
                stack: error.stack?.split('\n').slice(1, 2).join('\n').trim(),
            }
        }
        return {}
    } catch {
        return {}
    }
}

export const sendErrorToNelo = debounce((entry: string, error: Error, pageSet?: Maybe<PageSetType>) => {
    postLog({
        level: 'ERROR',
        type: entry,
        info: {
            ...getErrorTrace(error),
            pageSet,
        },
    })
    // ErrorBoundary 보다 Global 이 우선 호출되기 때문에, 여유 시간 확보
}, 100)

export const sendErrorBoundaryToNelo = debounce((entry: string, error: Error, pageSet?: Maybe<PageSetType>) => {
    postLog({
        level: 'FATAL',
        type: entry,
        info: {
            ...getErrorTrace(error),
            pageSet,
        },
    })
    // ErrorBoundary 보다 Global 이 우선 호출되기 때문에, 여유 시간 확보
}, 100)

/** 124번 line의 sendOrderFailToNelo와 뭐가 다른지 확인 필요 */
export const sendOrderFailureToNelo = sendToNelo<
    SendToNeloType<{
        orderSheetId?: string
        code: string
        message: string
    }>
>({
    level: 'INFO',
    type: 'OrderFailure',
})

export const sendOrderInitToNelo = sendToNelo<
    SendToNeloType<{
        message: string
    }>
>({
    level: 'INFO',
    type: 'OrderInit',
})

export const sendOrderRejectToNelo = sendToNelo<
    SendToNeloType<{
        orderSheetId?: string
        message: string
    }>
>({
    level: 'WARN',
    type: 'OrderReject',
})

export const sendOrderTryToNelo = sendToNelo<
    SendToNeloType<{
        orderSheetId?: string
        code: string
    }>
>({
    level: 'INFO',
    type: 'OrderTry',
})

export const sendOrderFailToNelo = sendToNelo<
    SendToNeloType<{
        orderSheetId?: string
        orderNo?: string
        orderProcessId?: string
        code: string
        message: string
        level?: LogLevelType
    }>
>({
    level: 'WARN',
    type: 'OrderStop',
})

export const sendOrderApplyFailToNelo = sendToNelo<
    SendToNeloType<{
        orderNo?: Maybe<string>
        orderSheetId?: Maybe<string>
        orderProcessId?: string
        code?: string
        message?: string
        level?: LogLevelType
    }>
>({
    level: 'INFO',
    type: 'OrderApply',
})

export const sendTimestampToNelo = sendToNelo<
    SendToNeloType<{
        orderSheetId: Maybe<string>
        orderNo?: Maybe<string>
        orderProcessId?: string
        time?: Maybe<string>
        message?: string
    }>
>({
    level: 'INFO',
    type: 'OrderTimestamp',
})

/** 주문서를 떠나는 로그 수집 */
export const sendLeaveToNelo = sendToNelo<
    SendToNeloType<{
        orderSheetId?: string
        type: string
        message: string
        path?: Maybe<string>
    }>
>({
    level: 'INFO',
    type: 'Leave',
})

export const sendHopeDateChangeToNelo = sendToNelo<
    SendToNeloType<{
        level?: LogLevelType
        code?: string
        popupUrl?: string
        isSuccess?: boolean
        selectedDate?: string
        errorCode?: string
        errorMessage?: string
        errorData?: object
    }>
>({
    level: 'INFO',
    type: 'ChangeHopeDate',
})

/** pixel 로그 추적위한 nelo 수집 */
export const sendPixelToNelo = sendToNelo<
    SendToNeloType<{
        message?: string
        code?: string
        state?: string
        level?: LogLevelType
        success: boolean
        subscriptions?: {
            [channelNo: string]: boolean
        }
    }>
>({
    level: 'INFO',
    type: 'PixelPurchase',
})

/** criteo 로그 추적위한 nelo 수집 */
export const sendCriteoToNelo = sendToNelo<
    SendToNeloType<{
        message?: string
        code?: string
        state?: string
        level?: LogLevelType
        success: boolean
        subscriptions?: {
            [channelNo: string]: boolean
        }
    }>
>({
    level: 'INFO',
    type: 'CriteoPurchase',
})
